import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../services/axiosInstance';
import { sanitizeGame } from './game.slice';

const initialState = {
  loading: false,
  error: null,
  success: false,
  appliedPromo: null,
  stripeSessionUrl: null,
  razorpayOrder: null,
  myOrders: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    limit: 5
  }
};

// Async Thunk: Fetch Current User's Orders
export const fetchMyOrders = createAsyncThunk(
  'order/fetchMyOrders',
  async (params, { rejectWithValue }) => {
    try {
      const page = params?.page || 1;
      const limit = params?.limit || 5;
      const res = await axiosInstance.get(`/orders/my?page=${page}&limit=${limit}`);
      if (res.data.success) {
        return {
          orders: res.data.orders || [],
          pagination: res.data.pagination || { currentPage: 1, totalPages: 1, totalItems: 0, limit }
        };
      }
      return rejectWithValue('Failed to fetch orders');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Async Thunk: Validate Promo Code
export const validatePromoCode = createAsyncThunk(
  'order/validatePromoCode',
  async ({ code, orderValue }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post('/discounts/validate', { code, orderValue });
      if (res.data.success) {
        return res.data.data;
      }
      return rejectWithValue(res.data.message || 'Invalid discount code');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Async Thunk: Apply Promo Code
export const applyPromoCode = createAsyncThunk(
  'order/applyPromoCode',
  async ({ code, orderValue, items }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post('/discounts/apply', { code, orderValue, items });
      if (res.data.success) {
        return res.data.data;
      }
      return rejectWithValue(res.data.message || 'Invalid discount code');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Async Thunk: Create Stripe Session
export const createStripeSession = createAsyncThunk(
  'order/createStripeSession',
  async ({ items, tax, discountAmount, promoCode, successUrl, cancelUrl }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post('/orders/create-stripe-session', {
        items,
        tax,
        discountAmount,
        promoCode,
        successUrl,
        cancelUrl
      });
      if (res.data.success) {
        return res.data.url;
      }
      return rejectWithValue(res.data.message || 'Failed to create Stripe session');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Async Thunk: Verify Stripe Session
export const verifyStripeSession = createAsyncThunk(
  'order/verifyStripeSession',
  async ({ sessionId }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post('/orders/verify-stripe-session', { sessionId });
      if (res.data.success) {
        return res.data;
      }
      return rejectWithValue(res.data.message || 'Stripe verification failed');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Async Thunk: Create Razorpay Order
export const createRazorpayOrder = createAsyncThunk(
  'order/createRazorpayOrder',
  async ({ items, promoCode }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post('/orders/create-razorpay-order', { items, promoCode });
      if (res.data.success) {
        return { order: res.data.order, key_id: res.data.key_id };
      }
      return rejectWithValue(res.data.message || 'Failed to create Razorpay order');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Async Thunk: Create Standard Order / Verify Razorpay
export const createStandardOrder = createAsyncThunk(
  'order/createStandardOrder',
  async (orderPayload, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post('/orders', orderPayload);
      if (res.data.success) {
        return res.data;
      }
      return rejectWithValue(res.data.message || 'Order creation failed');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Async Thunk: Create Stripe Order
export const createStripeOrder = createAsyncThunk(
  'order/createStripeOrder',
  async (orderPayload, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post('/orders/create-stripe-order', orderPayload);
      if (res.data.success) {
        return res.data;
      }
      return rejectWithValue(res.data.message || 'Order creation failed');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Async Thunk: Create Stripe Payment Intent
export const createStripePaymentIntent = createAsyncThunk(
  'order/createStripePaymentIntent',
  async ({ items, promoCode }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post('/orders/create-stripe-payment-intent', { items, promoCode });
      if (res.data.success) {
        return res.data.clientSecret;
      }
      return rejectWithValue(res.data.message || 'Failed to create payment intent');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    clearOrderState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.appliedPromo = null;
      state.stripeSessionUrl = null;
      state.razorpayOrder = null;
    },
    removeAppliedPromo: (state) => {
      state.appliedPromo = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Validate Promo
      .addCase(validatePromoCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(validatePromoCode.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(validatePromoCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Apply Promo
      .addCase(applyPromoCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(applyPromoCode.fulfilled, (state, action) => {
        state.loading = false;
        state.appliedPromo = action.payload;
      })
      .addCase(applyPromoCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.appliedPromo = null;
      })
      // Create Stripe Session
      .addCase(createStripeSession.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createStripeSession.fulfilled, (state, action) => {
        state.loading = false;
        state.stripeSessionUrl = action.payload;
      })
      .addCase(createStripeSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Verify Stripe Session
      .addCase(verifyStripeSession.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyStripeSession.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(verifyStripeSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create Razorpay Order
      .addCase(createRazorpayOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createRazorpayOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.razorpayOrder = action.payload;
      })
      .addCase(createRazorpayOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create Standard Order
      .addCase(createStandardOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createStandardOrder.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(createStandardOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create Stripe Order
      .addCase(createStripeOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createStripeOrder.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(createStripeOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch My Orders
      .addCase(fetchMyOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMyOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.myOrders = (action.payload.orders || []).map(order => ({
          ...order,
          items: (order.items || []).map(item => ({
            ...item,
            game: sanitizeGame(item.game)
          }))
        }));
        state.pagination = action.payload.pagination || { currentPage: 1, totalPages: 1, totalItems: 0, limit: 5 };
      })
      .addCase(fetchMyOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearOrderState, removeAppliedPromo } = orderSlice.actions;
export default orderSlice.reducer;
